import React, { useEffect, useState } from 'react';
import { Card, Accordion, Table, Button, InputGroup, Form, Modal, Spinner } from 'react-bootstrap';
import usePalette from '../../../../../hooks/usePalette';
import { useSelector } from 'react-redux';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useDispatch } from 'react-redux';
import { isEmpty } from 'lodash';
import { Tooltip } from '@mui/material';
import Select from 'react-select';
import axios from '../../../../../utils/axios';
import moment from 'moment';

// Icons
import Icon from '@mdi/react';
import { mdiPencil, mdiCheckBold, mdiCloseThick, mdiHistory } from '@mdi/js';

// Options
import { PROJECT_TYPE_OPTIONS, PROJECT_CATEGORY_OPTIONS } from '../../../../../constants';

// redux
import { updateProject } from '../../../../../redux/slices/projects/projectSlice';

const ProjectBrief = ({ project, isAdminLevel, showNotification }) => {
  const palette = usePalette();
  const dispatch = useDispatch();

  const [isEdit, setIsEdit] = useState({
    program_id: false,
    main_project_id: false,
    section_upen_id: false,
    code: false,
    name: false,
    description: false,
    project_type_id: false,
  });
  const [programOptions, setProgramOptions] = useState([]);
  const [mainProjectOptions, setMainProjectOptions] = useState([]);
  const [departmentOptions, setDepartmentOptions] = useState([]);

  const [programValue, setProgramValue] = useState(null);
  const [mainProjectValue, setMainProjectValue] = useState(null);
  const [ministryValue, setMinistryValue] = useState(null);
  const [departmentValue, setDepartmentValue] = useState(null);
  const [executingAgencyValue, setExecutingAgencyValue] = useState(null);

  // History modal state
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [historyType, setHistoryType] = useState('');
  const [historyData, setHistoryData] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const {
    btnLoading,
    user,
    ministries,
    programs,
    mainProjects,
    upenSections,
    branches,
    categories,
  } = useSelector((state) => ({
    user: state.userReducer.user,
    btnLoading: state.projectReducer.btnLoading,
    programs: state.utilsReducer.dropdownItems.programs,
    mainProjects: state.utilsReducer.dropdownItems.mainProjects,

    ministries: state.utilsReducer.dropdownItems.ministries,
    upenSections: state.utilsReducer.dropdownItems.upen_sections,
    branches: state.utilsReducer.dropdownItems.branches,
    categories: state.utilsReducer.dropdownItems.categories,
  }));

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      code: project.code || '',
      branch_id: project.branch_id || '',
      section_upen_id: project.section_upen_id || '',
      name: project.name || '',
      description: project.description || '',
      project_type_id: project.project_type_id || '',
      project_category_id: project.project_category_id || '',
    },
    onSubmit: async (values) => {
      const newValues = {
        ...values,
        program_id: programValue?.id || null,
        main_project_id: mainProjectValue?.id || null,
        ministry_id: ministryValue?.code || null,
        department_id: departmentValue?.id || null,
        executing_agency: executingAgencyValue?.id || null,
        id: project.uuid,
      };

      await dispatch(updateProject(newValues));

      const hideInput = async (field) => {
        await new Promise((resolve) => {
          setIsEdit((prevIsEdit) => ({
            ...prevIsEdit,
            [field]: false,
          }));
          resolve();
        });
      };

      const { id, ...rest } = newValues;

      const exludeId = rest;

      const keys = Object.keys(exludeId);

      const hideAllInputs = async () => {
        for (const key of keys) {
          await hideInput(key);
        }
      };

      hideAllInputs();
      showNotification();
    },
  });

  const handleSaveForm = () => {
    formik.handleSubmit();
  };

  const handleEditField = (field) => {
    setIsEdit({
      ...isEdit,
      [field]: true,
    });
  };

  const handleCloseField = (field) => {
    setIsEdit({
      ...isEdit,
      [field]: false,
    });
  };

  const handleSaveInput = (field) => {
    formik.handleSubmit();
  };

  const handleSelectMinistry = (value) => {
    setMinistryValue(value);

    const departments = value ? value.departments : [];
    setDepartmentOptions(departments);
  };

  // Fetch history data
  const fetchHistory = async (changeType) => {
    setHistoryLoading(true);
    setHistoryType(changeType);
    setShowHistoryModal(true);

    try {
      const response = await axios.get(`/projects/${project.uuid}/activity-logs`, {
        params: {
          change_type: changeType,
        },
      });
      // The API returns data directly, not wrapped in a 'data' key
      const data = Array.isArray(response.data) ? response.data : response.data?.data || [];
      setHistoryData(data);
    } catch (error) {
      console.error('Error fetching history:', error);
      setHistoryData([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleOpenHistory = (type) => {
    fetchHistory(type);
  };

  const handleCloseHistoryModal = () => {
    setShowHistoryModal(false);
    setHistoryData([]);
    setHistoryType('');
  };

  const getHistoryTitle = () => {
    switch (historyType) {
      case 'ministry_change':
        return 'Sejarah Perubahan Kementerian';
      case 'department_change':
        return 'Sejarah Perubahan Jabatan';
      case 'executing_agency_change':
        return 'Sejarah Perubahan Agensi Pelaksana';
      default:
        return 'Sejarah Perubahan';
    }
  };

  useEffect(() => {
    if (isEmpty(project)) return;
    const filteredPrograms =
      programs?.filter((obj) => obj.ministry_code.toString() === project.ministry_id.toString()) ||
      [];

    setProgramOptions(filteredPrograms);

    const filteredMainProjects =
      mainProjects?.filter(
        (obj) => obj?.ministry_id?.toString() === project?.ministry_id?.toString()
      ) || [];

    const mainProjectWithCodeLabel = filteredMainProjects.map((obj) => ({
      ...obj,
      label: `${obj.code} ${obj.label}`,
    }));
    setMainProjectOptions(mainProjectWithCodeLabel);

    setProgramValue(project.program);
    setMainProjectValue(project.main_project);
    setMinistryValue(project.ministry);
    setDepartmentValue(project.department);

    setExecutingAgencyValue(project.executing_agency);

    setDepartmentOptions(project.ministry?.departments);
  }, [project, programs, mainProjects]);

  return (
    <React.Fragment>
      {/* History Modal */}
      <Modal show={showHistoryModal} onHide={handleCloseHistoryModal} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>{getHistoryTitle()}</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: '60vh', overflowY: 'auto' }}>
          {historyLoading ? (
            <div className="d-flex justify-content-center align-items-center py-5">
              <Spinner animation="border" variant="primary" />
            </div>
          ) : historyData.length === 0 ? (
            <div className="text-center py-5 text-muted">
              <Icon path={mdiHistory} size={2} color="#ccc" />
              <p className="mt-3">Tiada rekod perubahan</p>
            </div>
          ) : (
            <Table striped bordered hover size="sm">
              <thead>
                <tr>
                  <th style={{ width: '5%' }}>#</th>
                  <th style={{ width: '25%' }}>Tarikh & Masa</th>
                  <th style={{ width: '20%' }}>Pengguna</th>
                  <th style={{ width: '50%' }}>Perubahan</th>
                </tr>
              </thead>
              <tbody>
                {historyData.map((item, index) => {
                  const props = item.properties || {};
                  const oldAttrs = props.old || {};
                  const newAttrs = props.attributes || {};

                  return (
                    <tr key={item.id || index}>
                      <td align="center">{index + 1}</td>
                      <td>{moment(item.created_at).format('DD/MM/YYYY HH:mm')}</td>
                      <td>{item.causer?.name || '-'}</td>
                      <td>
                        <div>
                          <strong>{item.description}</strong>
                        </div>
                        {/* Show detailed change info if available */}
                        {props.old_ministry && props.new_ministry && (
                          <div className="mt-1">
                            <small className="text-muted">
                              <span className="text-danger">
                                D{props.old_ministry.code} - {props.old_ministry.name}
                              </span>
                              {' → '}
                              <span className="text-success">
                                D{props.new_ministry.code} - {props.new_ministry.name}
                              </span>
                            </small>
                          </div>
                        )}
                        {props.old_department && props.new_department && (
                          <div className="mt-1">
                            <small className="text-muted">
                              <span className="text-danger">
                                {props.old_department.code} - {props.old_department.name}
                              </span>
                              {' → '}
                              <span className="text-success">
                                {props.new_department.code} - {props.new_department.name}
                              </span>
                            </small>
                          </div>
                        )}
                        {props.old_executing_agency && props.new_executing_agency && (
                          <div className="mt-1">
                            <small className="text-muted">
                              <span className="text-danger">
                                {props.old_executing_agency.code} -{' '}
                                {props.old_executing_agency.name}
                              </span>
                              {' → '}
                              <span className="text-success">
                                {props.new_executing_agency.code} -{' '}
                                {props.new_executing_agency.name}
                              </span>
                            </small>
                          </div>
                        )}
                        {/* Fallback: show raw old/new values if no detailed info */}
                        {!props.old_ministry &&
                          !props.old_department &&
                          !props.old_executing_agency && (
                            <div className="mt-1">
                              {Object.keys(oldAttrs).length > 0 && (
                                <small className="text-muted d-block">
                                  <strong>Sebelum:</strong> {JSON.stringify(oldAttrs)}
                                </small>
                              )}
                              {Object.keys(newAttrs).length > 0 && (
                                <small className="text-muted d-block">
                                  <strong>Selepas:</strong> {JSON.stringify(newAttrs)}
                                </small>
                              )}
                            </div>
                          )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseHistoryModal}>
            Tutup
          </Button>
        </Modal.Footer>
      </Modal>

      <Accordion.Header>
        <span className="fw-bolder">PROJEK BRIEF</span>
      </Accordion.Header>
      <Accordion.Body className="border-primary border-2 border-start p-0">
        <Card className="mb-0">
          <Card.Body>
            <div className="px-3 py-2 table-responsive">
              <Table size="sm" striped className="m-0 project-overview">
                <tbody>
                  <tr>
                    <th>RMK</th>
                    <td>{`${project.rmk?.name} (${project.rmk?.short_form})`}</td>
                  </tr>
                  <tr>
                    <th>Kod Projek</th>
                    <td>
                      {isEdit.code ? (
                        <>
                          <InputGroup size="sm">
                            <Form.Control
                              type="text"
                              name="code"
                              value={formik.values.code}
                              onChange={formik.handleChange}
                              onBlur={formik.handleBlur}
                            />
                            <Button
                              className="mx-1"
                              variant="success"
                              onClick={() => handleSaveForm()}
                              disabled={btnLoading}
                            >
                              <Icon path={mdiCheckBold} size={0.7} />
                            </Button>
                            <Button variant="secondary" onClick={() => handleCloseField('code')}>
                              <Icon path={mdiCloseThick} size={0.7} />
                            </Button>
                          </InputGroup>
                        </>
                      ) : (
                        <div className="d-flex justify-content-between">
                          {`D${project.ministry?.code}-${project.department?.code}-000000-${
                            project.program?.code || 'XXXXXX'
                          }-${project.main_project?.code || 'XXXXXX'}${project.code}-00000`}
                          {isAdminLevel && (
                            <>
                              <Tooltip title="Ubah kod sub projek" placement="left" arrow>
                                <span>
                                  <Icon
                                    path={mdiPencil}
                                    size={0.7}
                                    color={palette.primary}
                                    onClick={() => handleEditField('code')}
                                    style={{ cursor: 'pointer' }}
                                  />
                                </span>
                              </Tooltip>
                            </>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                  <tr>
                    <th>Kementerian</th>
                    <td>
                      <div className="d-flex justify-content-between align-items-center">
                        {isEdit.ministry_id ? (
                          <InputGroup size="sm">
                            <div className="d-flex align-items-center w-100 gap-1">
                              <div className="flex-grow-1">
                                <Select
                                  className="w-100"
                                  isSearchable
                                  placeholder="Pilih Kementerian"
                                  options={ministries}
                                  value={ministryValue}
                                  onChange={(value) => handleSelectMinistry(value)}
                                  noOptionsMessage={() => 'Tiada pilihan'}
                                  components={{
                                    IndicatorSeparator: () => null,
                                  }}
                                  menuPortalTarget={document.body}
                                  styles={{
                                    control: (baseStyles, state) => ({
                                      ...baseStyles,
                                      borderColor: '#ced4da',
                                      backgroundColor: state.isDisabled ? '#e2e8ee' : '#FFF',
                                    }),
                                    singleValue: (baseStyles, state) => ({
                                      ...baseStyles,
                                      color: state.isDisabled ? 'rgb(84, 84, 84)' : '#495057',
                                    }),
                                    placeholder: (baseStyles, state) => ({
                                      ...baseStyles,
                                      color: state.isDisabled ? 'rgb(84, 84, 84)' : '#495057',
                                    }),
                                    menuPortal: (base) => ({
                                      ...base,
                                      zIndex: 9999,
                                    }),
                                  }}
                                />
                              </div>
                              <Button
                                variant="success"
                                onClick={() => handleSaveForm()}
                                disabled={btnLoading}
                              >
                                <Icon path={mdiCheckBold} size={0.7} />
                              </Button>
                              <Button
                                variant="secondary"
                                onClick={() =>
                                  setIsEdit({
                                    ...isEdit,
                                    ministry_id: false,
                                  })
                                }
                              >
                                <Icon path={mdiCloseThick} size={0.7} />
                              </Button>
                            </div>
                          </InputGroup>
                        ) : (
                          <>
                            {project?.ministry
                              ? `D${project?.ministry?.code} - ${project?.ministry?.name}`
                              : '-'}
                            <div className="d-flex gap-2">
                              {isAdminLevel && (
                                <Tooltip title="Lihat Sejarah Perubahan" placement="top" arrow>
                                  <span>
                                    <Icon
                                      path={mdiHistory}
                                      size={0.7}
                                      color={palette.secondary}
                                      onClick={() => handleOpenHistory('ministry_change')}
                                      style={{ cursor: 'pointer' }}
                                    />
                                  </span>
                                </Tooltip>
                              )}
                              {isAdminLevel && (
                                <Tooltip title="Ubah Kementerian" placement="left" arrow>
                                  <span>
                                    <Icon
                                      path={mdiPencil}
                                      size={0.7}
                                      color={palette.primary}
                                      onClick={() =>
                                        setIsEdit({
                                          ...isEdit,
                                          ministry_id: true,
                                        })
                                      }
                                      style={{ cursor: 'pointer' }}
                                    />
                                  </span>
                                </Tooltip>
                              )}
                            </div>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <th>Jabatan</th>
                    <td>
                      <div className="d-flex justify-content-between align-items-center">
                        {isEdit.department_id ? (
                          <InputGroup size="sm">
                            <div className="d-flex align-items-center w-100 gap-1">
                              <div className="flex-grow-1">
                                <Select
                                  className="w-100"
                                  isSearchable
                                  isClearable
                                  placeholder="Pilih Jabatan"
                                  options={departmentOptions}
                                  value={departmentValue}
                                  onChange={(value) => setDepartmentValue(value)}
                                  noOptionsMessage={() => 'Tiada pilihan'}
                                  components={{
                                    IndicatorSeparator: () => null,
                                  }}
                                  menuPortalTarget={document.body}
                                  styles={{
                                    control: (baseStyles, state) => ({
                                      ...baseStyles,
                                      borderColor: '#ced4da',
                                      backgroundColor: state.isDisabled ? '#e2e8ee' : '#FFF',
                                    }),
                                    singleValue: (baseStyles, state) => ({
                                      ...baseStyles,
                                      color: state.isDisabled ? 'rgb(84, 84, 84)' : '#495057',
                                    }),
                                    placeholder: (baseStyles, state) => ({
                                      ...baseStyles,
                                      color: state.isDisabled ? 'rgb(84, 84, 84)' : '#495057',
                                    }),
                                    menuPortal: (base) => ({
                                      ...base,
                                      zIndex: 9999,
                                    }),
                                  }}
                                />
                              </div>
                              <Button
                                variant="success"
                                onClick={() => handleSaveForm()}
                                disabled={btnLoading}
                              >
                                <Icon path={mdiCheckBold} size={0.7} />
                              </Button>
                              <Button
                                variant="secondary"
                                onClick={() =>
                                  setIsEdit({
                                    ...isEdit,
                                    department_id: false,
                                  })
                                }
                              >
                                <Icon path={mdiCloseThick} size={0.7} />
                              </Button>
                            </div>
                          </InputGroup>
                        ) : (
                          <>
                            {project?.department
                              ? `${project?.department?.code} - ${project?.department?.value}`
                              : '-'}
                            <div className="d-flex gap-2">
                              {isAdminLevel && (
                                <Tooltip title="Lihat Sejarah Perubahan" placement="top" arrow>
                                  <span>
                                    <Icon
                                      path={mdiHistory}
                                      size={0.7}
                                      color={palette.secondary}
                                      onClick={() => handleOpenHistory('department_change')}
                                      style={{ cursor: 'pointer' }}
                                    />
                                  </span>
                                </Tooltip>
                              )}
                              {isAdminLevel && (
                                <Tooltip title="Ubah Jabatan" placement="left" arrow>
                                  <span>
                                    <Icon
                                      path={mdiPencil}
                                      size={0.7}
                                      color={palette.primary}
                                      onClick={() =>
                                        setIsEdit({
                                          ...isEdit,
                                          department_id: true,
                                        })
                                      }
                                      style={{ cursor: 'pointer' }}
                                    />
                                  </span>
                                </Tooltip>
                              )}
                            </div>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <th>Agensi Pelaksana (Kod Jabatan)</th>
                    <td>
                      <div className="d-flex justify-content-between align-items-center">
                        {isEdit.executing_agency ? (
                          <InputGroup size="sm">
                            <div className="d-flex align-items-center w-100 gap-1">
                              <div className="flex-grow-1">
                                <Select
                                  className="w-100"
                                  isSearchable
                                  placeholder="Pilih Agensi Pelaksana"
                                  options={departmentOptions}
                                  value={executingAgencyValue}
                                  onChange={(value) => setExecutingAgencyValue(value)}
                                  noOptionsMessage={() => 'Tiada pilihan'}
                                  components={{
                                    IndicatorSeparator: () => null,
                                  }}
                                  menuPortalTarget={document.body}
                                  styles={{
                                    control: (baseStyles, state) => ({
                                      ...baseStyles,
                                      borderColor: '#ced4da',
                                      backgroundColor: state.isDisabled ? '#e2e8ee' : '#FFF',
                                    }),
                                    singleValue: (baseStyles, state) => ({
                                      ...baseStyles,
                                      color: state.isDisabled ? 'rgb(84, 84, 84)' : '#495057',
                                    }),
                                    placeholder: (baseStyles, state) => ({
                                      ...baseStyles,
                                      color: state.isDisabled ? 'rgb(84, 84, 84)' : '#495057',
                                    }),
                                    menuPortal: (base) => ({
                                      ...base,
                                      zIndex: 9999,
                                    }),
                                  }}
                                />
                              </div>
                              <Button
                                variant="success"
                                onClick={() => handleSaveForm()}
                                disabled={btnLoading}
                              >
                                <Icon path={mdiCheckBold} size={0.7} />
                              </Button>
                              <Button
                                variant="secondary"
                                onClick={() =>
                                  setIsEdit({
                                    ...isEdit,
                                    executing_agency: false,
                                  })
                                }
                              >
                                <Icon path={mdiCloseThick} size={0.7} />
                              </Button>
                            </div>
                          </InputGroup>
                        ) : (
                          <>
                            {project?.ministry
                              ? `${project?.executing_agency?.code} - ${project?.executing_agency?.value}`
                              : '-'}
                            <div className="d-flex gap-2">
                              {isAdminLevel && (
                                <Tooltip title="Lihat Sejarah Perubahan" placement="top" arrow>
                                  <span>
                                    <Icon
                                      path={mdiHistory}
                                      size={0.7}
                                      color={palette.secondary}
                                      onClick={() => handleOpenHistory('executing_agency_change')}
                                      style={{ cursor: 'pointer' }}
                                    />
                                  </span>
                                </Tooltip>
                              )}
                              {isAdminLevel && (
                                <Tooltip title="Ubah Agensi Pelaksana" placement="left" arrow>
                                  <span>
                                    <Icon
                                      path={mdiPencil}
                                      size={0.7}
                                      color={palette.primary}
                                      onClick={() =>
                                        setIsEdit({
                                          ...isEdit,
                                          executing_agency: true,
                                        })
                                      }
                                      style={{ cursor: 'pointer' }}
                                    />
                                  </span>
                                </Tooltip>
                              )}
                            </div>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <th>Program</th>
                    <td>
                      <div className="d-flex justify-content-between align-items-center">
                        {isEdit.program_id ? (
                          <Select
                            className="w-100"
                            isSearchable
                            placeholder="Pilih Program"
                            options={programOptions}
                            value={programValue}
                            onChange={(value) => setProgramValue(value)}
                            noOptionsMessage={() => 'Tiada pilihan'}
                            components={{
                              IndicatorSeparator: () => null,
                            }}
                            menuPortalTarget={document.body}
                            styles={{
                              control: (baseStyles, state) => ({
                                ...baseStyles,
                                borderColor: '#ced4da',
                                backgroundColor: state.isDisabled ? '#e2e8ee' : '#FFF',
                              }),
                              singleValue: (baseStyles, state) => ({
                                ...baseStyles,
                                color: state.isDisabled ? 'rgb(84, 84, 84)' : '#495057', // Change text color to black when disabled
                              }),
                              placeholder: (baseStyles, state) => ({
                                ...baseStyles,
                                color: state.isDisabled ? 'rgb(84, 84, 84)' : '#495057', // Change placeholder color to black when disabled
                              }),
                              menuPortal: (base) => ({
                                ...base,
                                zIndex: 9999, // bring to front
                              }),
                            }}
                          />
                        ) : (
                          // <Form.Select
                          //   size='sm'
                          //   className='rounded-2'
                          //   name='program_id'
                          //   value={formik.values.program_id}
                          //   onChange={(e) => {
                          //     const value = e.target.value;
                          //     formik.setFieldValue("program_id", Number(value));
                          //     const options = mainProjects.filter(
                          //       (item) => item.program_id === Number(value),
                          //     );
                          //     setMainProjectOptions(options);
                          //   }}
                          // >
                          //   {programOptions.map((item) => (
                          //     <option key={item.id} value={item.id}>
                          //       {item.label}
                          //     </option>
                          //   ))}
                          // </Form.Select>
                          <>
                            {project.program
                              ? `${project.program.prefix}${project.ministry.code} ${project.program.code} - ${project.program.name}`
                              : '-'}
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <th>Projek Utama</th>
                    <td>
                      <div className="d-flex justify-content-between align-items-center">
                        {isEdit.main_project_id ? (
                          <InputGroup size="sm">
                            <div className="d-flex align-items-center w-100 gap-1">
                              <div className="flex-grow-1">
                                <Select
                                  className="w-100"
                                  isSearchable
                                  placeholder="Pilih Projek Utama"
                                  options={mainProjectOptions}
                                  value={mainProjectValue}
                                  onChange={(value) => setMainProjectValue(value)}
                                  noOptionsMessage={() => 'Tiada pilihan'}
                                  components={{
                                    IndicatorSeparator: () => null,
                                  }}
                                  menuPortalTarget={document.body}
                                  styles={{
                                    control: (baseStyles, state) => ({
                                      ...baseStyles,
                                      borderColor: '#ced4da',
                                      backgroundColor: state.isDisabled ? '#e2e8ee' : '#FFF',
                                    }),
                                    singleValue: (baseStyles, state) => ({
                                      ...baseStyles,
                                      color: state.isDisabled ? 'rgb(84, 84, 84)' : '#495057',
                                    }),
                                    placeholder: (baseStyles, state) => ({
                                      ...baseStyles,
                                      color: state.isDisabled ? 'rgb(84, 84, 84)' : '#495057',
                                    }),
                                    menuPortal: (base) => ({
                                      ...base,
                                      zIndex: 9999,
                                    }),
                                  }}
                                />
                              </div>
                              <Button
                                variant="success"
                                onClick={() => handleSaveForm()}
                                disabled={btnLoading}
                              >
                                <Icon path={mdiCheckBold} size={0.7} />
                              </Button>
                              <Button
                                variant="secondary"
                                onClick={() =>
                                  setIsEdit({
                                    ...isEdit,
                                    program_id: false,
                                    main_project_id: false,
                                  })
                                }
                              >
                                <Icon path={mdiCloseThick} size={0.7} />
                              </Button>
                            </div>
                          </InputGroup>
                        ) : (
                          <>
                            {project?.main_project
                              ? `${project?.main_project?.code} - ${project?.main_project?.name}`
                              : '-'}
                            {isAdminLevel && (
                              <>
                                <Tooltip title="Ubah Program & Projek Utama" placement="left" arrow>
                                  <span>
                                    <Icon
                                      path={mdiPencil}
                                      size={0.7}
                                      color={palette.primary}
                                      onClick={() =>
                                        setIsEdit({
                                          ...isEdit,
                                          program_id: true,
                                          main_project_id: true,
                                        })
                                      }
                                      style={{ cursor: 'pointer' }}
                                    />
                                  </span>
                                </Tooltip>
                              </>
                            )}
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <th>Cawangan (Jika Berkaitan)</th>
                    <td>
                      <div className="d-flex justify-content-between align-items-center">
                        {isEdit.branch_id ? (
                          <InputGroup size="sm">
                            <Form.Select
                              className="rounded-2"
                              name="branch_id"
                              value={formik.values.branch_id}
                              onChange={(e) => {
                                const value = e.target.value;
                                formik.setFieldValue('branch_id', Number(value));
                              }}
                            >
                              {branches.map((item) => (
                                <option key={item.id} value={item.id}>
                                  {item.label}
                                </option>
                              ))}
                            </Form.Select>
                            <Button
                              className="mx-1"
                              variant="success"
                              onClick={() => handleSaveForm()}
                              disabled={btnLoading}
                            >
                              <Icon path={mdiCheckBold} size={0.7} />
                            </Button>
                            <Button
                              variant="secondary"
                              onClick={() => {
                                setIsEdit({
                                  ...isEdit,
                                  branch_id: false,
                                });
                              }}
                            >
                              <Icon path={mdiCloseThick} size={0.7} />
                            </Button>
                          </InputGroup>
                        ) : (
                          <>
                            {project?.branch ? `${project.branch?.label}` : '-'}
                            {isAdminLevel && (
                              <>
                                <Tooltip title="Cawangan" placement="left" arrow>
                                  <span>
                                    <Icon
                                      path={mdiPencil}
                                      size={0.7}
                                      color={palette.primary}
                                      onClick={() =>
                                        setIsEdit({
                                          ...isEdit,
                                          branch_id: true,
                                        })
                                      }
                                      style={{ cursor: 'pointer' }}
                                    />
                                  </span>
                                </Tooltip>
                              </>
                            )}
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <th>Nama Projek</th>
                    <td>
                      {isEdit.name ? (
                        <>
                          <Form.Control
                            as="textarea"
                            type="text"
                            name="name"
                            className="mb-1"
                            style={{ minHeight: 75, maxHeight: 105 }}
                            value={formik.values.name}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                          />
                          <div className="d-flex justify-content-end">
                            <Button
                              size="sm"
                              className="mx-1"
                              variant="success"
                              onClick={() => handleSaveForm()}
                              disabled={btnLoading}
                            >
                              <Icon path={mdiCheckBold} size={0.7} />
                            </Button>
                            <Button
                              size="sm"
                              variant="secondary"
                              onClick={() => handleCloseField('name')}
                            >
                              <Icon path={mdiCloseThick} size={0.7} />
                            </Button>
                          </div>
                        </>
                      ) : (
                        <div className="d-flex justify-content-between">
                          {project.name}
                          {isAdminLevel && (
                            <>
                              <Tooltip title="Ubah Nama Projek" placement="left" arrow>
                                <span>
                                  <Icon
                                    path={mdiPencil}
                                    size={0.7}
                                    color={palette.primary}
                                    onClick={() => handleEditField('name')}
                                    style={{ cursor: 'pointer' }}
                                  />
                                </span>
                              </Tooltip>
                            </>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                  <tr>
                    <th>Keterangan Projek</th>
                    <td>
                      {isEdit.description ? (
                        <>
                          <Form.Control
                            as="textarea"
                            type="text"
                            name="description"
                            className="mb-1"
                            style={{ minHeight: 130, maxHeight: 150 }}
                            value={formik.values.description}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                          />
                          <div className="d-flex justify-content-end">
                            <Button
                              size="sm"
                              className="mx-1"
                              variant="success"
                              onClick={() => handleSaveForm()}
                              disabled={btnLoading}
                            >
                              <Icon path={mdiCheckBold} size={0.7} />
                            </Button>
                            <Button
                              size="sm"
                              variant="secondary"
                              onClick={() => handleCloseField('description')}
                            >
                              <Icon path={mdiCloseThick} size={0.7} />
                            </Button>
                          </div>
                        </>
                      ) : (
                        <div className="d-flex justify-content-between">
                          {project.description}

                          <>
                            <Tooltip title="Ubah Keterangan Projek" placement="left" arrow>
                              <span>
                                <Icon
                                  path={mdiPencil}
                                  size={0.7}
                                  color={palette.primary}
                                  onClick={() => handleEditField('description')}
                                  style={{ cursor: 'pointer' }}
                                />
                              </span>
                            </Tooltip>
                          </>
                        </div>
                      )}
                    </td>
                  </tr>
                  <tr>
                    <th>Jenis Projek</th>
                    <td>
                      <div className="d-flex justify-content-between align-items-center">
                        {isEdit.project_type_id ? (
                          <InputGroup size="sm">
                            <Form.Select
                              className="rounded-2"
                              name="project_type_id"
                              value={formik.values.project_type_id}
                              onChange={(e) => {
                                const value = e.target.value;
                                formik.setFieldValue('project_type_id', Number(value));
                              }}
                            >
                              {PROJECT_TYPE_OPTIONS.map((item) => (
                                <option key={item.id} value={item.id}>
                                  {item.name}
                                </option>
                              ))}
                            </Form.Select>
                            <Button
                              className="mx-1"
                              variant="success"
                              onClick={() => handleSaveForm()}
                              disabled={btnLoading}
                            >
                              <Icon path={mdiCheckBold} size={0.7} />
                            </Button>
                            <Button
                              variant="secondary"
                              onClick={() => handleCloseField('project_type_id')}
                            >
                              <Icon path={mdiCloseThick} size={0.7} />
                            </Button>
                          </InputGroup>
                        ) : (
                          <>
                            {project?.project_type?.name}
                            {isAdminLevel && (
                              <>
                                <Tooltip title="Ubah jenis projek" placement="left" arrow>
                                  <span>
                                    <Icon
                                      path={mdiPencil}
                                      size={0.7}
                                      color={palette.primary}
                                      onClick={() => handleEditField('project_type_id')}
                                      style={{ cursor: 'pointer' }}
                                    />
                                  </span>
                                </Tooltip>
                              </>
                            )}
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <th>Kategori Projek</th>
                    <td>
                      <div className="d-flex justify-content-between align-items-center">
                        {isEdit.project_category_id ? (
                          <InputGroup size="sm">
                            <Form.Select
                              className="rounded-2"
                              name="project_category_id"
                              value={formik.values.project_category_id}
                              onChange={(e) => {
                                const value = e.target.value;
                                formik.setFieldValue('project_category_id', Number(value));
                              }}
                            >
                              {categories.map((item) => (
                                <option key={item.id} value={item.id}>
                                  {item.name}
                                </option>
                              ))}
                            </Form.Select>
                            <Button
                              className="mx-1"
                              variant="success"
                              onClick={() => handleSaveForm()}
                              disabled={btnLoading}
                            >
                              <Icon path={mdiCheckBold} size={0.7} />
                            </Button>
                            <Button
                              variant="secondary"
                              onClick={() => handleCloseField('project_category_id')}
                            >
                              <Icon path={mdiCloseThick} size={0.7} />
                            </Button>
                          </InputGroup>
                        ) : (
                          <>
                            {project?.project_category?.value}
                            {isAdminLevel && (
                              <>
                                <Tooltip title="Ubah jenis projek" placement="left" arrow>
                                  <span>
                                    <Icon
                                      path={mdiPencil}
                                      size={0.7}
                                      color={palette.primary}
                                      onClick={() => handleEditField('project_category_id')}
                                      style={{ cursor: 'pointer' }}
                                    />
                                  </span>
                                </Tooltip>
                              </>
                            )}
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <th>Seksyen UPEN</th>
                    <td>
                      <div className="d-flex justify-content-between align-items-center">
                        {isEdit.section_upen_id ? (
                          <InputGroup size="sm">
                            <Form.Select
                              className="rounded-2"
                              name="section_upen_id"
                              value={formik.values.section_upen_id}
                              onChange={(e) => {
                                const value = e.target.value;
                                formik.setFieldValue('section_upen_id', Number(value));
                              }}
                            >
                              {upenSections.map((item) => (
                                <option key={item.id} value={item.id}>
                                  {item.label} ({item.code})
                                </option>
                              ))}
                            </Form.Select>
                            <div className="d-flex justify-content-end">
                              <Button
                                size="sm"
                                className="mx-1"
                                variant="success"
                                onClick={() => handleSaveForm()}
                                disabled={btnLoading}
                              >
                                <Icon path={mdiCheckBold} size={0.7} />
                              </Button>
                              <Button
                                size="sm"
                                variant="secondary"
                                onClick={() => handleCloseField('section_upen_id')}
                              >
                                <Icon path={mdiCloseThick} size={0.7} />
                              </Button>
                            </div>
                          </InputGroup>
                        ) : (
                          <>
                            {project.section_upen_id ? project?.upen_section?.name : '-'}
                            {isAdminLevel && (
                              <>
                                <Tooltip title="Ubah Seksyen UPEN" placement="left" arrow>
                                  <span>
                                    <Icon
                                      path={mdiPencil}
                                      size={0.7}
                                      color={palette.primary}
                                      onClick={() => handleEditField('section_upen_id')}
                                      style={{ cursor: 'pointer' }}
                                    />
                                  </span>
                                </Tooltip>
                              </>
                            )}
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <th>Sektor</th>
                    <td>{project.main_sector?.name}</td>
                  </tr>
                </tbody>
              </Table>
            </div>
          </Card.Body>
        </Card>
      </Accordion.Body>
    </React.Fragment>
  );
};

export default ProjectBrief;
